/**
 * Shared core types. This file is a CONTRACT used by the simulation (src/core),
 * the sim worker, the client and the server. Changes must be ADDITIVE only
 * (new optional fields / new union members) so other modules keep compiling.
 *
 * Everything here must be plain-data / JSON-serialisable (except typed arrays
 * in protocol updates) because it crosses worker and network boundaries.
 */

import type { ScenarioDef } from "./scenario/types";

/** 0 = nobody / terra nullius. Real players are 1..65534. */
export type PlayerID = number;
export const NO_PLAYER: PlayerID = 0;

/** Index into the map grid: y * width + x. */
export type TileRef = number;

export type UnitID = number;

/** Stable client identity (random string chosen by the client, persisted in localStorage). */
export type ClientID = string;

// ---------------------------------------------------------------------------
// Terrain
// ---------------------------------------------------------------------------

export enum TerrainType {
  Ocean = 0,
  Lake = 1,
  Plains = 2,
  Highland = 3,
  Mountain = 4,
}

/** Wave 4: climate zone of a tile (biome byte bits 0..2, see src/core/map/terrain.ts). */
export enum Climate {
  Temperate = 0,
  Desert = 1,
  /** Cold land incl. ice; on water: cold / polar sea. */
  Tundra = 2,
  Tropical = 3,
  Steppe = 4,
}

/** Wave 4: resource deposit on a land tile (biome byte bits 3..5). 0 = none. */
export enum ResourceType {
  None = 0,
  Grain = 1,
  Wood = 2,
  Stone = 3,
  Metal = 4,
  Oil = 5,
  /** Herds and horses (livestock / wild game). */
  Animals = 6,
}

/**
 * Wave 4 stage I (module economy2, docs/GAME.md#resources-extractors-and-stockpiles): the five stockpiles every human and
 * nation keeps ("Your land fills five stockpiles; building, war machines and troops use them up.").
 * Food comes from grain, animals and fish, wood / stone / metal from their deposits, fuel from oil
 * (and coal from metal mines). Arrays indexed by stock kind use this order (food = 0 … fuel = 4).
 */
export type StockKind = "food" | "wood" | "stone" | "metal" | "fuel";

/** Stage K aidiplomacy (docs/GAME.md#negotiations-and-reputation): the kinds of offers between players. */
export type OfferKind = "peace" | "trade" | "jointAttack";
export const OFFER_KINDS: readonly OfferKind[] = ["peace", "trade", "jointAttack"];

/**
 * Stage K aidiplomacy: the terms of an offer (intent `offerPropose`, PlayerUpdate.negotiation).
 *  - peace: both may not attack each other for OFFERS.peaceTicks;
 *  - trade: stock against stock (and / or gold);
 *  - jointAttack: both attack `target` within OFFERS.jointWindowTicks.
 * `gold` is paid by the proposer, `askGold` by the receiver, on acceptance (integers >= 0).
 */
export interface OfferTerms {
  kind: OfferKind;
  gold?: number;
  askGold?: number;
  /** trade: stock the proposer gives (`giveAmount` > 0) … */
  give?: StockKind;
  giveAmount?: number;
  /** … and the stock it asks for (`takeAmount` > 0). */
  take?: StockKind;
  takeAmount?: number;
  /** jointAttack: the third player both attack. */
  target?: PlayerID;
}
export const STOCK_KINDS: readonly StockKind[] = ["food", "wood", "stone", "metal", "fuel"];

export const CLIMATES: readonly Climate[] = [Climate.Temperate, Climate.Desert, Climate.Tundra, Climate.Tropical, Climate.Steppe];
export const RESOURCE_TYPES: readonly ResourceType[] = [
  ResourceType.Grain,
  ResourceType.Wood,
  ResourceType.Stone,
  ResourceType.Metal,
  ResourceType.Oil,
  ResourceType.Animals,
];

/**
 * Wave 4 stage C: season of the year, a pure function of the tick (src/core/economy/seasons.ts
 * `seasonAt`). Games start in spring after the spawn phase.
 */
export enum Season {
  Spring = 0,
  Summer = 1,
  Autumn = 2,
  Winter = 3,
}

export const SEASONS_IN_ORDER: readonly Season[] = [Season.Spring, Season.Summer, Season.Autumn, Season.Winter];

// ---------------------------------------------------------------------------
// Players
// ---------------------------------------------------------------------------

export enum PlayerType {
  /** Real person (local or remote). */
  Human = "human",
  /** Simple "tribe" AI that mostly expands and attacks weak neighbours. */
  Bot = "bot",
  /** Smart AI named after a country / region, builds, trades, allies, nukes. */
  Nation = "nation",
}

export enum Difficulty {
  Easy = "easy",
  Medium = "medium",
  Hard = "hard",
  Impossible = "impossible",
}

export enum GameMode {
  FFA = "ffa",
  Teams = "teams",
}

/** AI personality used by Nation AI (improvement over the original game). */
export enum AIPersonality {
  Balanced = "balanced",
  Aggressive = "aggressive",
  Builder = "builder",
  Turtle = "turtle",
  Trader = "trader",
  Nuker = "nuker",
}

// ---------------------------------------------------------------------------
// Units & structures
// ---------------------------------------------------------------------------

export enum UnitType {
  // mobile units
  TransportShip = "transport_ship",
  Warship = "warship",
  TradeShip = "trade_ship",
  Shell = "shell",
  Train = "train",
  // missiles
  AtomBomb = "atom_bomb",
  HydrogenBomb = "hydrogen_bomb",
  MIRV = "mirv",
  MIRVWarhead = "mirv_warhead",
  SAMMissile = "sam_missile",
  // structures
  City = "city",
  Port = "port",
  DefensePost = "defense_post",
  MissileSilo = "missile_silo",
  SAMLauncher = "sam_launcher",
  Factory = "factory",
  /** Wave 4 P2: boosts the owner's deposits within EXTRACTOR.radius (src/core/economy/extractor.ts). */
  Extractor = "extractor",
  /**
   * Wave 4 P7: the free, unique capital building every player gets on its spawn tile
   * (src/core/empire/capital.ts). A structure, but never built, upgraded or demolished by intents.
   */
  Capital = "capital",
  /**
   * Wave 4 P2 (module ships, docs/GAME.md#automatic-navy-the-naval-stance): cheap, fast, weak warship that hunts transports
   * and trade ships and escorts convoys; built at any port.
   */
  PatrolBoat = "patrol_boat",
  /** Wave 4 P2: expensive, slow, heavily armoured warship that shells enemy coasts; needs a shipyard. */
  Battleship = "battleship",
  /** Wave 4 P2: structure next to an own port that builds battleships (structures/shipyard.ts). */
  Shipyard = "shipyard",
  /**
   * Wave 4 stage E (module airforce, docs/GAME.md#air-force): launches air strikes and
   * special-forces drops (src/core/executions/air); one sortie slot per level.
   */
  Airfield = "airfield",
  /** Wave 4 stage E: a strike plane flying from an airfield to its target area (sourceTile → targetTile). */
  StrikePlane = "strike_plane",
  /** Wave 4 stage E: a transport plane carrying special forces (`troops`) to its drop zone. */
  TransportPlane = "transport_plane",
}

export const STRUCTURE_TYPES: readonly UnitType[] = [
  UnitType.City,
  UnitType.Port,
  UnitType.DefensePost,
  UnitType.MissileSilo,
  UnitType.SAMLauncher,
  UnitType.Factory,
  UnitType.Extractor,
  UnitType.Capital,
  UnitType.Shipyard,
  // Wave 4 stage E (module airforce).
  UnitType.Airfield,
];

export const NUKE_TYPES: readonly UnitType[] = [UnitType.AtomBomb, UnitType.HydrogenBomb, UnitType.MIRV];

/** Units a player can order to be built from the build menu (structures + warship). */
export const BUILDABLE_TYPES: readonly UnitType[] = [
  // The capital (wave 4 P7) is placed by the spawn, never built.
  ...STRUCTURE_TYPES.filter((t) => t !== UnitType.Capital),
  UnitType.Warship,
  // Wave 4 P2 (module ships): built at a port / a shipyard like the warship.
  UnitType.PatrolBoat,
  UnitType.Battleship,
  UnitType.AtomBomb,
  UnitType.HydrogenBomb,
  UnitType.MIRV,
];

export function isStructure(t: UnitType): boolean {
  return STRUCTURE_TYPES.includes(t);
}

export function isNuke(t: UnitType): boolean {
  return NUKE_TYPES.includes(t) || t === UnitType.MIRVWarhead;
}

// ---------------------------------------------------------------------------
// Game settings
// ---------------------------------------------------------------------------

export interface GameSettings {
  /**
   * Map identifier. Either the id of a prebuilt map in /public/maps
   * (e.g. "world", "europe") or a procedural map spec:
   * "procgen:<style>:<size>:<seed>"  e.g. "procgen:continents:medium:12345"
   * (see src/core/map/procgen.ts).
   */
  mapId: string;
  mode: GameMode;
  /** Number of teams in Teams mode (2..8). Ignored in FFA. */
  teamCount: number;
  difficulty: Difficulty;
  /** Number of tribe bots (0..400). */
  bots: number;
  /** Spawn the map's nations as AI players. */
  nations: boolean;
  /**
   * Owner feedback (wave 4): at most this many of the map's nations take part (the strongest
   * first). Optional for backwards compatibility; undefined = 50.
   */
  nationCount?: number;
  /** Master seed for all simulation randomness. */
  seed: number;
  /** Seconds of the initial spawn-selection phase. */
  spawnPhaseSeconds: number;
  /**
   * Owner feedback (wave 4): singleplayer games have no spawn countdown; the spawn phase ends as
   * soon as every human has picked a spawn (spawnPhaseSeconds is ignored). Undefined = false.
   */
  instantSpawn?: boolean;
  /**
   * Stage L (module community, docs/GAME.md#community-and-moderation): co-op in Teams mode ("Your party is one
   * team against the AI nations."): every human plays on team 0, the AI on the other teams.
   * Undefined = false.
   */
  coop?: boolean;
  /** % of all land tiles a player/team must own to win (default 80). */
  winTerritoryPercent: number;
  /** Optional hard time limit in minutes; when hit, biggest territory wins. 0 = none. */
  timeLimitMinutes: number;
  infiniteGold: boolean;
  infiniteTroops: boolean;
  instantBuild: boolean;
  disableNukes: boolean;
  /** Unit types that may not be built in this game. */
  disabledUnits: UnitType[];
  /** Rendering-side fog of war (sim stays fully deterministic & complete). */
  fogOfWar: boolean;
  /** Allow free-text chat (multiplayer). Quick-chat is always enabled. */
  allowChat: boolean;
  /** Multiplier on the whole economy / growth pace (0.5 .. 2). Default 1. */
  gameSpeed: number;
  // Wave 4 systems (docs/GAME.md#feature-toggles). Optional for backwards compatibility:
  // undefined means ON. Read them with featureOn(settings, "<name>").
  /** High ground, chokepoints, beachheads and island defence modify combat. */
  strategicTerrain?: boolean;
  /** Climates change growth and combat per tile. */
  climate?: boolean;
  /** Resource deposits give bonuses and boost trade. */
  resources?: boolean;
  /**
   * Wave 4 stage I (module economy2, docs/GAME.md#resources-extractors-and-stockpiles): five stockpiles (food, wood, stone,
   * metal, fuel) filled by owned deposits and spent automatically on building, war machines and
   * troops. Needs `resources` too; off = the old gold-only economy.
   */
  stockpiles?: boolean;
  /** Seasons cycle and modify climates / economy. */
  seasons?: boolean;
  /** Artillery / armor / air strikes / special forces. */
  attackTypes?: boolean;
  /** Wave 4 P10: huge empires get restless far from their capital; restless land rebels. */
  unrest?: boolean;
  /** Wave 4 P11: gold can hire troops at once (escalating price). */
  mercenaries?: boolean;
  /** Wave 4 P16 (docs/GAME.md#passage-rights): allies may let your armies march through their land. */
  passage?: boolean;
  /** Wave 4 stage D (module ships): warships and battleships shell enemy land near the coast. */
  bombardment?: boolean;
  /**
   * Wave 4 stage D (3) + P5 (module navycontrol, docs/GAME.md#naval-warfare): blockades, strait
   * control, sea superiority at landings and convoys ("Rule the sea to block trade and land armies").
   */
  navalWarfare?: boolean;
  /**
   * Wave 4 stage D (4) (module fallout, docs/TECHNICAL.md#live-terrain): nukes leave craters and crater
   * lakes, contaminate deposits, disable structures by EMP, make the victim panic, can cause a
   * nuclear winter and anger every nation ("Nukes scar the land, the economy and the whole world").
   */
  nukeEffects?: boolean;
  /**
   * Wave 4 stage I (module weapons, docs/GAME.md#modern-weapons): napalm, cluster bombs, bunker busters,
   * defoliant and sea mines ("Weapons with consequences: they burn, poison and sink the economy
   * of your enemy."), and MIRV warheads by silo count.
   */
  weapons?: boolean;
  /**
   * Wave 4 P13 (module diplomacy2, docs/GAME.md#diplomacy-plus-pacts-tribute-and-vassals): non-aggression pacts, tribute demands
   * and vassals ("More options than war or alliance.").
   */
  diplomacyPlus?: boolean;
  /**
   * Wave 4 allywar (docs/GAME.md#allied-wars-coalitions-and-standing, toggle added by the followups module): allied war
   * requests, joining a friend's war, the coalition bonus and the standing overlay ("Ask your
   * ally to attack, and fight side by side for a bonus.").
   */
  allyWar?: boolean;
  /**
   * Wave 4 P12 (src/core/world/events.ts): how often world events (plague, gold rush, storm at
   * sea, spring floods, volcanic eruption) happen. Undefined = "normal"; read it with
   * worldEventFrequency(settings).
   */
  worldEvents?: WorldEventFrequency;
  /** Wave 4 P8 (src/core/world/wonders.ts): unique landmark tiles with a big bonus for their holder. */
  wonders?: boolean;
  /**
   * Wave 4 P9 (src/core/world/victory.ts): extra ways to win, all OFF by default (territory /
   * last standing / time limit always apply). Wonder victory: hold `wonderVictoryCount` wonders
   * (0 / undefined = a majority of the map's wonders) for `wonderVictoryMinutes` minutes.
   */
  wonderVictory?: boolean;
  wonderVictoryCount?: number;
  wonderVictoryMinutes?: number;
  /** Wave 4 P9: king of the hill — hold most of the marked centre zone for `hillVictoryMinutes` minutes. */
  hillVictory?: boolean;
  hillVictoryMinutes?: number;
  /** Wave 4 P9: economic victory — bank `economicVictoryGold` gold. */
  economicVictory?: boolean;
  economicVictoryGold?: number;
  /**
   * Wave 4 P14 (src/core/world/risingSea.ts, docs/GAME.md#rising-sea): the Rising Sea game mode
   * and its pace ("The ocean rises — the coasts are disappearing."). Undefined = "off"; read it
   * with risingSeaPace(settings).
   */
  risingSea?: RisingSeaPace;
  /**
   * Wave 4 stage J (module modes, docs/GAME.md#rulesets-game-modes-and-setup): the ruleset. "simple" = the classic set
   * (SIMPLE_FEATURES), "full" = every system but Diplomacy plus (FULL_FEATURES, stage O), "custom" =
   * the host's own toggles (absent = on).
   * Undefined (games made before stage J) = the stored toggles as they are (shown as Full / Custom).
   * Resolve it with featureOn / effectiveToggles (src/core/rules/ruleset.ts), never read it raw.
   */
  ruleset?: RulesetId;
  /**
   * Wave 4 stage J: the game mode (how a game is won). Replaces the separate victory switches:
   * the mode sets wonderVictory / hillVictory / economicVictory / risingSea. Undefined (games made
   * before stage J) = those switches as stored. Teams stays the separate `mode` (FFA / Teams).
   */
  winMode?: WinModeId;
  /**
   * Wave 4 stage J: biome / weather warfare zones (desert attrition, mud, jungle, forest cover,
   * winter tundra; src/core/combat/terrainEffects.ts) on top of strategic terrain. Off in Simple
   * ("strategic terrain light"). Undefined = on.
   */
  terrainWarfare?: boolean;
  /**
   * Wave 4 stage K (module aistrategy, docs/GAME.md#strategy): singleplayer only, off by default.
   * The AI nations adapt within bounds (aggression +-30 %) when the human wins too easily or keeps
   * losing. Ignored with more than one human.
   */
  dynamicDifficulty?: boolean;
  /**
   * Stage L (module progression, docs/GAME.md#progression): the weekly challenge and its
   * exclusive modifiers (src/core/challenges). Only challenge games carry it (the server derives
   * it); lobby settings with it are rejected (src/shared/validate.ts).
   */
  challenge?: ChallengeSpec;
  /**
   * Campaign 3 (src/core/scenario, docs/TECHNICAL.md#scenario-engine): a mission's pre-set start,
   * scripted triggers and goals. Singleplayer only: lobby settings with it are rejected
   * (src/shared/validate.ts). Replays carry it, so a mission replays exactly.
   */
  scenario?: ScenarioDef;
}

/** Stage L (module progression): exclusive weekly challenge modifiers, in display order. */
export type ChallengeModifierId =
  | "earthquakes"
  | "wildfires"
  | "floods"
  | "meteorShower"
  | "plagueWaves"
  | "goldRush"
  | "doubleEconomy"
  | "permanentFog"
  | "shrinkingWorld"
  | "lightning"
  | "taxStorms"
  | "leaderTax";

/** Stage L (module progression): the weekly challenge a game belongs to ("2026-W43") and its modifiers. */
export interface ChallengeSpec {
  week: string;
  modifiers: ChallengeModifierId[];
}

/** Wave 4 stage J (module modes): ruleset ids, in picker order. */
export type RulesetId = "simple" | "full" | "custom";
export const RULESET_IDS: readonly RulesetId[] = ["simple", "full", "custom"];

/** Wave 4 stage J (module modes): game modes (how a game is won), in picker order. */
export type WinModeId = "conquest" | "wonderRace" | "risingSea" | "kingOfTheHill" | "economic";
export const WIN_MODE_IDS: readonly WinModeId[] = ["conquest", "wonderRace", "risingSea", "kingOfTheHill", "economic"];

/**
 * Wave 4 stage J: the feature toggles of the Simple ruleset ("plays like the classic genre").
 * On: strategic terrain (light: no warfare zones), alliances, capitals, nukes (atom + hydrogen).
 * Everything else is off. Units of disabled systems: SIMPLE_DISABLED_UNITS (src/core/rules/ruleset.ts).
 */
export const SIMPLE_FEATURES: Readonly<Record<FeatureToggle, boolean>> = {
  strategicTerrain: true,
  terrainWarfare: false,
  climate: false,
  resources: false,
  stockpiles: false,
  seasons: false,
  attackTypes: false,
  unrest: false,
  mercenaries: false,
  bombardment: false,
  wonders: false,
  passage: false,
  navalWarfare: false,
  nukeEffects: false,
  diplomacyPlus: false,
  allyWar: false,
  weapons: false,
};

/**
 * Stage O (module simplify, owner-approved P1, docs/GAME.md#simple-full-and-custom): the feature toggles of the
 * Full ruleset. Every system except "Diplomacy plus" (pacts, tribute, vassals, war requests and
 * passage), which only Custom can switch on: the play tests found its stream of offers confusing.
 */
export const FULL_FEATURES: Readonly<Record<FeatureToggle, boolean>> = {
  strategicTerrain: true,
  terrainWarfare: true,
  climate: true,
  resources: true,
  stockpiles: true,
  seasons: true,
  attackTypes: true,
  unrest: true,
  mercenaries: true,
  bombardment: true,
  wonders: true,
  passage: false,
  navalWarfare: true,
  nukeEffects: true,
  diplomacyPlus: false,
  allyWar: false,
  weapons: true,
};

/**
 * Stage O (module simplify, owner-approved P2): toggles merged into another switch. War requests
 * (`allyWar`) and passage rights (`passage`) are part of "Diplomacy plus" (`diplomacyPlus`): one
 * switch in the Custom panel. The old fields still load; in a Custom game the three are on only
 * when none of them is stored off (featureOn). Settings without a ruleset (made before stage J)
 * read each field as stored, so their replays play as before.
 */
export const MERGED_TOGGLES: Readonly<Partial<Record<FeatureToggle, FeatureToggle>>> = { allyWar: "diplomacyPlus", passage: "diplomacyPlus" };

/** Wave 4 stage J: systems a game mode needs whatever the ruleset says ("implies Full rules for them"). */
export const WIN_MODE_FEATURES: Readonly<Record<WinModeId, readonly FeatureToggle[]>> = {
  conquest: [],
  wonderRace: ["wonders"],
  risingSea: [],
  kingOfTheHill: [],
  economic: [],
};

/** Wave 4 P14: pace of the Rising Sea mode ("off" = the coasts stay). */
export type RisingSeaPace = "off" | "slow" | "normal" | "fast";
export const RISING_SEA_PACES: readonly RisingSeaPace[] = ["off", "slow", "normal", "fast"];

/**
 * Wave 4 P14: the Rising Sea pace of a game (default "off": the mode is optional). Stage J: with a
 * game mode set, only the Rising Sea mode rises (its pace defaults to "normal").
 */
export function risingSeaPace(settings: GameSettings): RisingSeaPace {
  const v = settings.risingSea;
  const pace: RisingSeaPace = v === "slow" || v === "normal" || v === "fast" ? v : "off";
  const m = settings.winMode;
  if (m === undefined) return pace;
  if (m !== "risingSea") return "off";
  return pace === "off" ? "normal" : pace;
}

/** Wave 4 P8: kinds of wonder (src/core/world/wonders.ts). */
export type WonderKind = "ruins" | "monument" | "harbour" | "sacredMountain" | "oasis" | "worldTree";
export const WONDER_KINDS: readonly WonderKind[] = ["ruins", "monument", "harbour", "sacredMountain", "oasis", "worldTree"];

/** How a game ended (wave 4 P9 adds "wonders", "hill" and "economic"). */
/** "scenario": a Campaign 3 mission decided its own end (src/core/scenario; won = `winner` is the human). */
export type GameOverReasonType = "territory" | "lastStanding" | "timeLimit" | "surrender" | "wonders" | "hill" | "economic" | "scenario";

/** Wave 4 P12: frequency setting of the world events ("off" = none). */
export type WorldEventFrequency = "off" | "rare" | "normal" | "chaos";
export const WORLD_EVENT_FREQUENCIES: readonly WorldEventFrequency[] = ["off", "rare", "normal", "chaos"];

/**
 * Wave 4 P12: the world event frequency of a game (default "normal"). Stage J: none in the Simple
 * ruleset; "normal" in Full; the host's choice in Custom (and in games made before stage J).
 */
export function worldEventFrequency(settings: GameSettings): WorldEventFrequency {
  const r = settings.ruleset;
  if (r === "simple") return "off";
  if (r === "full") return "normal";
  const v = settings.worldEvents;
  return v === "off" || v === "rare" || v === "chaos" ? v : "normal";
}

export type FeatureToggle = "strategicTerrain" | "climate" | "resources" | "seasons" | "attackTypes" | "unrest" | "mercenaries" | "bombardment" | "wonders" | "passage" | "navalWarfare" | "nukeEffects" | "diplomacyPlus" | "allyWar" | "stockpiles" | "weapons" | "terrainWarfare";

/**
 * Wave 4 feature toggles default to ON when absent. Stage J (module modes): the ruleset decides
 * first (Simple = SIMPLE_FEATURES, Full = FULL_FEATURES, Custom / none = the stored toggle), and a
 * game mode turns on the systems it needs (WIN_MODE_FEATURES). Stage O (module simplify): in Custom
 * the merged toggles share one value (MERGED_TOGGLES). Cheap: safe on hot paths.
 */
export function featureOn(settings: GameSettings, feature: FeatureToggle): boolean {
  const r = settings.ruleset;
  if (r === "full" ? FULL_FEATURES[feature] : r === "simple" ? SIMPLE_FEATURES[feature] : storedToggleOn(settings, feature, r === "custom")) return true;
  const m = settings.winMode;
  return m !== undefined && WIN_MODE_FEATURES[m] !== undefined && WIN_MODE_FEATURES[m].includes(feature);
}

/**
 * A stored toggle (absent = on). With `merged` (the Custom ruleset) "Diplomacy plus" and the
 * toggles merged into it are one switch: on only while none of the three is stored off.
 */
function storedToggleOn(settings: GameSettings, feature: FeatureToggle, merged: boolean): boolean {
  if (merged && (feature === "diplomacyPlus" || feature === "allyWar" || feature === "passage")) {
    return settings.diplomacyPlus !== false && settings.allyWar !== false && settings.passage !== false;
  }
  return settings[feature] !== false;
}

export const DEFAULT_GAME_SETTINGS: GameSettings = {
  mapId: "world",
  mode: GameMode.FFA,
  teamCount: 2,
  difficulty: Difficulty.Medium,
  bots: 400,
  nationCount: 50,
  nations: true,
  seed: 1,
  spawnPhaseSeconds: 10,
  winTerritoryPercent: 80,
  timeLimitMinutes: 0,
  infiniteGold: false,
  infiniteTroops: false,
  instantBuild: false,
  disableNukes: false,
  disabledUnits: [],
  fogOfWar: false,
  allowChat: true,
  gameSpeed: 1,
  strategicTerrain: true,
  climate: true,
  resources: true,
  seasons: true,
  attackTypes: true,
};

/** A human player registered by the lobby/server before the game starts. */
export interface PlayerInfo {
  playerID: PlayerID;
  clientID: ClientID;
  name: string;
  /** Optional ISO-3166 alpha-2 code or emoji flag chosen by the player. */
  flag?: string;
  /** Team index (0-based) in Teams mode; assigned by the server/lobby. */
  team?: number;
}

// ---------------------------------------------------------------------------
// Intents: the ONLY way players influence the simulation.
// Every intent is stamped with the issuing player's id by the server
// (or LocalTransport in singleplayer) and applied at the start of a tick.
// ---------------------------------------------------------------------------

export type QuickChatKey =
  | "attack_player" // param: player id
  | "help"
  | "ally_please"
  | "thanks"
  | "sorry"
  | "gg"
  | "good_luck"
  | "nuke_incoming"
  | "focus_target" // param: player id
  | "dont_attack"
  | "truce"
  | "betrayal"
  | "build_ports"
  | "need_troops"
  | "need_gold";

export type PingKind = "attack" | "defend" | "danger" | "look";

/** Wave 4 navycontrol: standing orders of a warship (`moveWarship.order`). */
export type WarshipOrder = "patrol" | "hold" | "escort";
export const WARSHIP_ORDERS: readonly WarshipOrder[] = ["patrol", "hold", "escort"];

/**
 * Wave 4 stage J (module naval, docs/GAME.md#automatic-navy-the-naval-stance): "Choose a naval stance; your shipyards build
 * the fleet and it fights by itself." None = no new warships (the ones you have guard your ports),
 * Coastal defence = a small fleet guards your ports and coast and escorts your boats and trade,
 * Sea control = a big fleet that also blockades enemy ports, holds straits and shells enemy coasts
 * in a war (src/core/naval). Set with the `navalStance` intent.
 */
export type NavalStance = "none" | "coastal" | "seaControl";
export const NAVAL_STANCES: readonly NavalStance[] = ["none", "coastal", "seaControl"];

/**
 * Wave 4 stage E (module attacktypes, docs/HISTORY.md): "Choose how you attack:
 * infantry, artillery or armor." Every land attack keeps the mode it was started with
 * (src/core/combat/attackModes.ts). Air strikes and special forces are separate intents
 * (module airforce).
 */
export type AttackMode = "infantry" | "artillery" | "armor";
export const ATTACK_MODES: readonly AttackMode[] = ["infantry", "artillery", "armor"];

/**
 * Wave 4 stage I (module army, docs/GAME.md#army-mix-and-recruitment-presets): "The game picks the units: your recruitment
 * preset sets the mix of infantry, artillery and armor." Since stage I the attack intent's `mode`
 * is ignored (old replays); every land attack uses the player's mix automatically
 * (src/core/combat/armyMix.ts). `custom` uses the weights of the `armyPreset` intent.
 */
export type ArmyPreset = "defensive" | "balanced" | "offensive" | "custom";
export const ARMY_PRESETS: readonly ArmyPreset[] = ["defensive", "balanced", "offensive", "custom"];

/**
 * Wave 4 stage I (module weapons, docs/GAME.md#modern-weapons): tactical weapons, each with its own
 * consequence (src/core/executions/weapons). Array order = the index used in TickUpdate.weapons.
 */
export type WeaponKind = "napalm" | "cluster" | "bunkerBuster" | "defoliant" | "seaMines";
export const WEAPON_KINDS: readonly WeaponKind[] = ["napalm", "cluster", "bunkerBuster", "defoliant", "seaMines"];

/**
 * Wave 4 allywar: why a war request was declined (nations give a reason; "expired" = no answer
 * in time, set by the simulation).
 */
export type WarDeclineReason = "noTrust" | "likesTarget" | "tooStrong" | "busy" | "unreachable" | "notNow" | "expired";
export const WAR_DECLINE_REASONS: readonly WarDeclineReason[] = ["noTrust", "likesTarget", "tooStrong", "busy", "unreachable", "notNow", "expired"];

export type Intent =
  /** Choose / move spawn location during the spawn phase. */
  | { type: "spawn"; tile: TileRef }
  /**
   * Land attack. targetID 0 = expand into unowned land.
   * `ratio` (0..1] of the attacker's current troops is committed when the intent executes.
   * `targetTile` is an optional hint used to bias the front towards that point.
   * `mode` (wave 4 stage E, module attacktypes): ignored since stage I (the army mix is automatic,
   * module army); kept so old intents and replays still validate.
   */
  | { type: "attack"; targetID: PlayerID; ratio: number; targetTile?: TileRef; mode?: AttackMode }
  /** Retreat an ongoing attack; surviving troops return (minus a retreat penalty). */
  | { type: "cancelAttack"; attackID: number }
  /** Naval invasion: send a transport ship carrying ratio*troops towards a coastal tile. */
  | { type: "boat"; targetTile: TileRef; ratio: number; sourceTile?: TileRef }
  | { type: "cancelBoat"; unitID: UnitID }
  /**
   * Build a structure – tile is where the player clicked. Stage J (module naval): warships, patrol
   * boats and battleships are built by the naval stance only; a `build` of one is refused.
   */
  | { type: "build"; unitType: UnitType; tile: TileRef }
  /** Upgrade (stack a level on) an existing structure. */
  | { type: "upgrade"; unitID: UnitID }
  /** Launch an atom bomb / hydrogen bomb / MIRV from the nearest ready silo. */
  | { type: "nuke"; unitType: UnitType; targetTile: TileRef }
  /**
   * Order a warship to move / patrol around a water tile. Wave 4 navycontrol (optional, additive):
   * `order` "hold" keeps it on station at `tile` (strait control, blockades); "escort" makes it
   * follow own transport ship `escortID` (`tile` = where that transport is). Absent = "patrol".
   * Stage J (module naval): IGNORED by the simulation (the naval stance commands every warship);
   * kept so old intents and replays still validate.
   */
  | { type: "moveWarship"; unitID: UnitID; tile: TileRef; order?: WarshipOrder; escortID?: UnitID }
  /**
   * Wave 4 stage J (module naval, docs/GAME.md#automatic-navy-the-naval-stance): choose the naval stance (what the
   * shipyards build and what the fleet does by itself).
   */
  | { type: "navalStance"; stance: NavalStance }
  /** Demolish one of your own structures / units. */
  | { type: "deleteUnit"; unitID: UnitID }
  | { type: "allianceRequest"; targetID: PlayerID }
  | { type: "allianceReply"; requesterID: PlayerID; accept: boolean }
  | { type: "allianceExtend"; targetID: PlayerID }
  | { type: "breakAlliance"; targetID: PlayerID }
  | { type: "embargo"; targetID: PlayerID; enabled: boolean }
  /** Embargo / un-embargo everyone at once. */
  | { type: "embargoAll"; enabled: boolean }
  | { type: "donateTroops"; targetID: PlayerID; ratio: number }
  | { type: "donateGold"; targetID: PlayerID; amount: number }
  /** targetID null = broadcast to everybody. `emoji` is an index into EMOJIS (src/core/emojis.ts). */
  | { type: "emoji"; targetID: PlayerID | null; emoji: number }
  | { type: "quickChat"; targetID: PlayerID | null; key: QuickChatKey; param?: PlayerID }
  /** Free text chat (only when settings.allowChat). scope "team" = allies + teammates. */
  | { type: "chat"; scope: "all" | "team"; text: string }
  /** Mark a player as your target (shown to allies/teammates). */
  | { type: "target"; targetID: PlayerID }
  /** Change the desired troop share of the population (0.05..1). */
  | { type: "troopRatio"; ratio: number }
  /** Map ping visible to allies/teammates. */
  | { type: "ping"; tile: TileRef; kind: PingKind }
  /** Give up: all territory becomes neutral, player is eliminated. */
  | { type: "surrender" }
  /** Wave 4 allywar: ask an ally / teammate to attack `targetID` (docs/GAME.md#allied-wars-coalitions-and-standing). */
  | { type: "requestAttack"; allyID: PlayerID; targetID: PlayerID }
  /** Wave 4 allywar: answer `requesterID`'s request to attack `targetID` (`reason` only when declining). */
  | { type: "answerAttackRequest"; requesterID: PlayerID; targetID: PlayerID; accept: boolean; reason?: WarDeclineReason }
  /** Wave 4 allywar: join an ally's war against `targetID` on your own (announces the commitment). */
  | { type: "joinWar"; allyID: PlayerID; targetID: PlayerID }
  /** Wave 4 P11: hire mercenaries (gold → troops at once; docs/GAME.md#unrest-and-rebellions). */
  | { type: "mercenaries" }
  /** Wave 4 P16 (docs/GAME.md#passage-rights): ask the ally `targetID` for passage through its land. */
  | { type: "requestPassage"; targetID: PlayerID }
  /** Wave 4 P16: answer `requesterID`'s passage request (accept = let its armies through your land). */
  | { type: "answerPassage"; requesterID: PlayerID; accept: boolean }
  /** Wave 4 P16: take back the passage you granted to `targetID` (its marching troops go home). */
  | { type: "revokePassage"; targetID: PlayerID }
  /**
   * Wave 4 stage E (airforce, docs/GAME.md#air-force): air strike on the area around `targetTile`
   * from the nearest ready own airfield in range.
   */
  | { type: "airStrike"; targetTile: TileRef }
  /**
   * Wave 4 stage E (airforce): drop special forces (`ratio` of the troops, capped by the airfield
   * level) on land around `targetTile`, near your own land; no shared border needed.
   */
  | { type: "airDrop"; targetTile: TileRef; ratio: number }
  /**
   * Wave 4 stage I (army, docs/GAME.md#army-mix-and-recruitment-presets): choose the recruitment preset; with "custom",
   * the weights `infantry`, `artillery`, `armor` (integers 0..100, all three or none) set the mix.
   */
  | { type: "armyPreset"; preset: ArmyPreset; infantry?: number; artillery?: number; armor?: number }
  /**
   * Wave 4 P13 (docs/GAME.md#diplomacy-plus-pacts-tribute-and-vassals): offer `targetID` a non-aggression pact (accepts its
   * open offer; inside the renewal window of a running pact it asks to renew the pact).
   */
  | { type: "pactRequest"; targetID: PlayerID }
  /** Wave 4 P13: answer `requesterID`'s pact offer. */
  | { type: "pactReply"; requesterID: PlayerID; accept: boolean }
  /** Wave 4 P13: break the non-aggression pact with `targetID` (traitor for a short while). */
  | { type: "pactBreak"; targetID: PlayerID }
  /** Wave 4 P13: "pay `amount` gold or I attack" (deadline DIPLOMACY2.tributeDeadlineTicks). */
  | { type: "tributeDemand"; targetID: PlayerID; amount: number }
  /** Wave 4 P13: answer `demanderID`'s tribute demand (accept = pay now). */
  | { type: "tributeReply"; demanderID: PlayerID; accept: boolean }
  /**
   * Wave 4 P13: vassalage. `asOverlord` true = offer `targetID` to become your vassal; false = ask
   * `targetID` for protection (become its vassal). The other side answers with `vassalReply`.
   */
  | { type: "vassalRequest"; targetID: PlayerID; asOverlord: boolean }
  /** Wave 4 P13: answer `requesterID`'s vassalage offer / request for protection. */
  | { type: "vassalReply"; requesterID: PlayerID; accept: boolean }
  /** Wave 4 P13: an overlord sets its vassal `targetID` free. */
  | { type: "vassalRelease"; targetID: PlayerID }
  /** Wave 4 P13: a vassal rises against its overlord (no traitor mark, a short war bonus). */
  | { type: "vassalRebel" }
  /**
   * Stage K aidiplomacy (docs/GAME.md#negotiations-and-reputation): offer `targetID` a deal (peace for tribute, a
   * stockpile trade or a joint attack on a third player); it expires unanswered. Diplomacy plus only.
   */
  | { type: "offerPropose"; targetID: PlayerID; terms: OfferTerms }
  /** Stage K aidiplomacy: accept or decline the open offer `offerID` made to you. */
  | { type: "offerReply"; offerID: number; accept: boolean }
  /** Stage K aidiplomacy: withdraw your own open offer `offerID`. */
  | { type: "offerCancel"; offerID: number }
  /**
   * Wave 4 P14 (Rising Sea games only): move the capital to the highest dry own land near it
   * (the simulation picks the site; allowed while the capital stands on land that will flood).
   */
  | { type: "moveCapital" }
  /**
   * Stage J capital (docs/GAME.md#capitals): after your capital fell, declare one of your
   * completed cities (`tile`; absent = the simulation picks your safest, biggest city) your
   * provisional capital for gold (allowed CAPITAL.provisionalDelaySeconds after the fall, once).
   */
  | { type: "provisionalCapital"; tile?: TileRef }
  /**
   * Wave 4 stage I (module weapons): fire `weapon` at `targetTile` (bombs fly from the nearest own
   * silo or airfield in range; sea mines are laid from the nearest own port).
   */
  | { type: "weapon"; weapon: WeaponKind; targetTile: TileRef }
  /** Client disconnected / reconnected. Inserted by the server. */
  | { type: "markDisconnected"; disconnected: boolean };

export type IntentType = Intent["type"];

export interface StampedIntent {
  playerID: PlayerID;
  intent: Intent;
}

/** One simulation tick worth of input. turn === tick number it executes on. */
export interface Turn {
  turn: number;
  intents: StampedIntent[];
}

// ---------------------------------------------------------------------------
// Messages / events emitted by the simulation
// ---------------------------------------------------------------------------

export type MessageSeverity = "info" | "success" | "warning" | "danger" | "chat";

/**
 * Events are emitted by the sim during a tick and shipped to the client in TickUpdate.events.
 * `to` = recipient player id; undefined = everybody.
 * Text is NOT stored in the sim: `key` is an i18n key (src/client/i18n) and `params` its values.
 */
export type GameEvent =
  | { kind: "message"; to?: PlayerID; severity: MessageSeverity; key: string; params?: Record<string, string | number> }
  | { kind: "allianceRequest"; from: PlayerID; to: PlayerID; expiresAtTick: number }
  | { kind: "allianceAccepted"; a: PlayerID; b: PlayerID; expiresAtTick: number }
  | { kind: "allianceRejected"; from: PlayerID; to: PlayerID }
  | { kind: "allianceBroken"; traitor: PlayerID; victim: PlayerID }
  | { kind: "allianceExpired"; a: PlayerID; b: PlayerID }
  | { kind: "allianceExtendRequest"; from: PlayerID; to: PlayerID }
  | { kind: "emoji"; from: PlayerID; to: PlayerID | null; emoji: number }
  | { kind: "quickChat"; from: PlayerID; to: PlayerID | null; key: QuickChatKey; param?: PlayerID }
  | { kind: "chat"; from: PlayerID; scope: "all" | "team"; text: string }
  | { kind: "ping"; from: PlayerID; tile: TileRef; pingKind: PingKind }
  | { kind: "attackStarted"; attacker: PlayerID; defender: PlayerID; troops: number }
  | { kind: "boatLanded"; owner: PlayerID; tile: TileRef; target: PlayerID }
  | { kind: "nukeLaunched"; owner: PlayerID; unitType: UnitType; from: TileRef; target: TileRef; unitID: UnitID; impactTick: number }
  | {
      kind: "nukeExploded";
      owner: PlayerID;
      unitType: UnitType;
      tile: TileRef;
      radius: number;
      /** MIRV warheads: unit id of the parent MIRV, shared by every warhead of one salvo (lets the UI merge log lines). */
      salvo?: UnitID;
      /** Optional impact summary for the event log. */
      tilesDestroyed?: number;
      victims?: PlayerID[];
    }
  | { kind: "nukeIntercepted"; owner: PlayerID; interceptor: PlayerID; tile: TileRef; salvo?: UnitID }
  | { kind: "structureBuilt"; owner: PlayerID; unitType: UnitType; tile: TileRef; unitID: UnitID }
  | { kind: "unitDestroyed"; owner: PlayerID; unitType: UnitType; tile: TileRef; by?: PlayerID }
  | { kind: "unitCaptured"; unitType: UnitType; from: PlayerID; to: PlayerID; tile: TileRef }
  | { kind: "tradeCompleted"; from: PlayerID; to: PlayerID; gold: number; tile: TileRef }
  | { kind: "donation"; from: PlayerID; to: PlayerID; troops?: number; gold?: number }
  | { kind: "embargo"; from: PlayerID; to: PlayerID; enabled: boolean }
  | { kind: "targetSet"; from: PlayerID; target: PlayerID }
  | { kind: "playerEliminated"; player: PlayerID; by?: PlayerID }
  | { kind: "playerSpawned"; player: PlayerID; tile: TileRef }
  | { kind: "spawnPhaseEnded" }
  /** Wave 4 allywar: `from` asks its ally `to` to attack `target`. */
  | { kind: "warRequest"; from: PlayerID; to: PlayerID; target: PlayerID; expiresAtTick: number }
  /** Wave 4 allywar: `to` answered `from`'s war request; accepted → it fights `target` until `untilTick`. */
  | { kind: "warReply"; from: PlayerID; to: PlayerID; target: PlayerID; accept: boolean; reason?: WarDeclineReason; untilTick?: number }
  /** Wave 4 allywar: `player` joins its ally's war against `target` on its own until `untilTick`. */
  | { kind: "warJoined"; player: PlayerID; ally: PlayerID; target: PlayerID; untilTick: number }
  /** Wave 4 allywar: `helper` attacked `enemy`, who is at war with `helper`'s friend `ally` (`ally`'s bond grows). */
  | { kind: "allyHelped"; helper: PlayerID; ally: PlayerID; enemy: PlayerID }
  /** Wave 4 allywar: `player` and its friend `partner` attack `target` together (coalition bonus). */
  | { kind: "coalitionFormed"; player: PlayerID; partner: PlayerID; target: PlayerID }
  | { kind: "gameOver"; winner?: PlayerID; winningTeam?: number; reason: GameOverReasonType }
  /**
   * Wave 4 P8 (src/core/world/wonders.ts): wonder `wonder` (index into TickUpdate.world.wonders,
   * of `wonderKind`, centre `tile`) changed hands from `from` to `player` (0 = nobody: unowned
   * land, or the wonder was destroyed when `destroyed`).
   */
  | { kind: "wonderCaptured"; wonder: number; wonderKind: WonderKind; tile: TileRef; player: PlayerID; from: PlayerID; destroyed?: boolean }
  /**
   * Wave 4 P3 (src/core/empire/plunder.ts): `player` gained `gold` by plundering a captured
   * structure (`unitType`, at `tile`) of `from`, or as the bounty for eliminating `from`
   * (source "bounty"; `tile` = the victim's spawn tile, or -1).
   */
  | { kind: "plunder"; player: PlayerID; from: PlayerID; gold: number; tile: TileRef; source: "capture" | "bounty"; unitType?: UnitType }
  /**
   * Wave 4 stage D (module ships, naval/bombardment.ts): a warship / battleship (`unitType`,
   * `unitID`) of `owner` fired a shell from `from` at the enemy land tile `to`; it lands on
   * `impactTick` (visual flight on the client).
   */
  | { kind: "bombardFired"; owner: PlayerID; unitType: UnitType; unitID: UnitID; from: TileRef; to: TileRef; impactTick: number }
  /**
   * Wave 4 stage D (module ships): a coastal bombardment shell of `owner` hit `victim`'s land at
   * `tile` (blast `radius` in tiles): `troops` of the victim died; `structure` = what happened to
   * the structure (`unitType`) on the hit tiles, if any. Never changes the owner of land.
   */
  | {
      kind: "bombardHit";
      owner: PlayerID;
      victim: PlayerID;
      tile: TileRef;
      radius: number;
      troops: number;
      unitType?: UnitType;
      structure?: "damaged" | "levelLost" | "destroyed";
    }
  /**
   * Wave 4 stage E (airforce, docs/GAME.md#air-force): a strike plane of `owner` (flown from the
   * airfield at `from`) bombed the area of `radius` tiles around `tile`: `troopsKilled` troops of
   * the `victims` died and `structuresHit` structures lost a level (or were destroyed).
   */
  | { kind: "airStrike"; owner: PlayerID; from: TileRef; tile: TileRef; radius: number; victims: PlayerID[]; troopsKilled: number; structuresHit: number }
  /**
   * Wave 4 stage E (airforce): an aircraft (`unitType`) of `owner` was shot down over `tile` by a
   * SAM of `interceptor`; a transport plane loses its `troops`.
   */
  | { kind: "aircraftDown"; owner: PlayerID; interceptor: PlayerID; unitType: UnitType; tile: TileRef; troops?: number }
  /**
   * Wave 4 stage E (airforce): `troops` special forces of `owner` landed at `tile` and attack
   * `target`'s land (0 = they claim unowned land).
   */
  | { kind: "airDrop"; owner: PlayerID; tile: TileRef; target: PlayerID; troops: number }
  /** Wave 4 stage I (module weapons): `owner` fired `weapon` from `from` at `target`; it lands on `impactTick`. */
  | { kind: "weaponLaunched"; owner: PlayerID; weapon: WeaponKind; from: TileRef; target: TileRef; impactTick: number }
  /**
   * Wave 4 stage I (module weapons): `weapon` of `owner` hit the area of `radius` tiles around `tile`:
   * `troopsKilled` troops of the `victims` died, `tiles` tiles burnt / defoliated (napalm, defoliant;
   * sea mines: the tiles of the new minefield), `unitType` = the structure a bunker buster destroyed.
   */
  | { kind: "weaponHit"; owner: PlayerID; weapon: WeaponKind; tile: TileRef; radius: number; victims: PlayerID[]; troopsKilled: number; tiles: number; unitType?: UnitType }
  /** Wave 4 stage I (module weapons): a sea mine of `owner` hit a ship (`unitType`) of `victim` at `tile`. */
  | { kind: "mineHit"; owner: PlayerID; victim: PlayerID; unitType: UnitType; tile: TileRef; sunk: boolean }
  /**
   * Wave 4 stage K (module aitactics, docs/BALANCE.md#head-to-head-brains-at-equal-economy): nation `player` used `tactic` against
   * `target` (0 = nobody) around `tile` (-1 = no spot). At most one per (player, tactic, target) a minute.
   */
  | { kind: "aiTactic"; player: PlayerID; target: PlayerID; tactic: AITacticKind; tile: TileRef };

/** Wave 4 stage K (module aitactics): tactic names of the `aiTactic` event (docs/BALANCE.md#head-to-head-brains-at-equal-economy). */
export type AITacticKind =
  | "cutoff"
  | "encirclement"
  | "highGround"
  | "weakestFront"
  | "bridgehead"
  | "defenceLine"
  | "raid"
  | "feint"
  | "pincer"
  | "capitalStrike"
  | "timingStrike"
  | "regroup";

export type GameEventKind = GameEvent["kind"];
